/**
 * useYearEndData Hook
 *
 * 단순한 로직:
 * 1. Firestore에 데이터 있으면 → 그대로 사용
 * 2. 없으면 → 새로 계산해서 저장
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { useUser } from '../contexts/UserContext';
import {
  useThreadsState,
  useThreadsDispatch,
  loadFriendsInteractionData,
} from '../contexts/ThreadsContext';
import { useFriendsInteractionData } from './useThreadsSelector';
import {
  createOrUpdateYearEnd,
  getYearEndById,
  getMyYearEnd,
  incrementYearEndViewCount,
} from '../utils/yearEndCache';
import {
  computeYearEndSummary,
  computeFriendsOfTheYear,
} from '../utils/yearEndCalculator';
import { computeActivitySummary } from '../utils/activityCalculator';
import { enrichFriendsWithProfilePictures } from '../utils/friendProfileFetch';

export const LOADING_STAGES = {
  THREADS: { id: 'threads', progress: 40, messageKey: 'yearEnd.loading.analyzingThreads' },
  FRIENDS: { id: 'friends', progress: 70, messageKey: 'yearEnd.loading.analyzingFriends' },
  FRIENDS_AVATAR: { id: 'friends_avatar', progress: 100, messageKey: 'yearEnd.loading.loadingAvatars' },
};

/**
 * useYearEndData Hook
 */
export const useYearEndData = (year = 2025, sharedId = null) => {
  const { profile } = useUser();
  const { lifetimeThreadsList, isLifetimeReady } = useThreadsState();
  const { repliesByUser, isDataReady: isFriendsReady } = useFriendsInteractionData();
  const dispatch = useThreadsDispatch();

  const isMyPage = !sharedId;

  // 상태
  const [yearEndData, setYearEndData] = useState(null);
  const [yearEndId, setYearEndId] = useState(sharedId || null);
  const [status, setStatus] = useState('loading');
  const [isPublished, setIsPublished] = useState(false);
  const [firestoreChecked, setFirestoreChecked] = useState(false);
  const [loadingState, setLoadingState] = useState({
    stage: LOADING_STAGES.THREADS,
    sampleTexts: [],
    totalThreads: 0,
    dateRange: null,
    friendsAnalyzing: [],
  });

  const computeStartedRef = useRef(false);
  const loadingStateUpdatedRef = useRef(false);

  const userId = useMemo(() => {
    if (profile?.id) return String(profile.id);
    return localStorage.getItem('userID') || null;
  }, [profile?.id]);

  // === 공유 페이지 모드 ===
  useEffect(() => {
    if (isMyPage) return;

    const fetchSharedData = async () => {
      setStatus('loading');
      try {
        const data = await getYearEndById(sharedId);
        if (!data) {
          setStatus('not_found');
          return;
        }

        const isOwner = userId && data.userId === userId;
        if (!isOwner && !data.isPublic) {
          setYearEndData({ username: data.username });
          setStatus('private');
          return;
        }

        if (!isOwner) {
          incrementYearEndViewCount(sharedId).catch(() => {});
        }

        setYearEndData(data);
        setYearEndId(data.id);
        setIsPublished(true);
        setStatus('ready');
      } catch (error) {
        console.error('Failed to fetch shared year-end:', error);
        setStatus('not_found');
      }
    };

    fetchSharedData();
  }, [sharedId, isMyPage, userId]);

  // === 내 페이지 모드: 1단계 - Firestore 확인 ===
  useEffect(() => {
    if (!isMyPage) return;
    if (!userId) {
      // userId 아직 로드 안 됨 - 대기 (unauthorized로 바꾸지 않음)
      return;
    }
    if (firestoreChecked) return; // 이미 확인함

    const checkFirestore = async () => {
      setStatus('loading'); // Firestore 조회 시작 - 로딩 상태 유지
      console.log('🔍 [Step 1] Checking Firestore...');

      const existingData = await getMyYearEnd(userId, year);

      if (existingData) {
        // 🚨 있으면 그대로 사용. 끝!
        console.log('✅ [Step 1] Data exists! Using as-is. Done.');
        setYearEndData(existingData);
        setYearEndId(existingData.id);
        setIsPublished(true);
        setStatus('ready');
        setFirestoreChecked(true); // 더 이상 아무것도 안함
        return;
      }

      // 없으면 계산 필요
      console.log('📊 [Step 1] No data. Need to compute.');
      setFirestoreChecked(true); // 다음 단계로
    };

    checkFirestore();
  }, [userId, year, isMyPage, firestoreChecked]);

  // === 로딩 상태 업데이트 (lifetimeThreadsList 준비되면 즉시) ===
  useEffect(() => {
    if (!isMyPage) return;
    if (status === 'ready') return; // 이미 완료됨
    if (loadingStateUpdatedRef.current) return;
    if (!lifetimeThreadsList || lifetimeThreadsList.length === 0) return;

    // lifetimeThreadsList가 준비되면 즉시 로딩 상태 업데이트
    const yearThreads = lifetimeThreadsList.filter(t => {
      if (!t.timestamp) return false;
      return new Date(t.timestamp).getFullYear() === year;
    });

    const sampleTexts = yearThreads.slice(0, 12).map(t => t.text || '').filter(Boolean);
    const timestamps = yearThreads.map(t => new Date(t.timestamp)).filter(d => !isNaN(d));
    const dateRange = timestamps.length > 0 ? {
      start: new Date(Math.min(...timestamps)).toISOString().split('T')[0],
      end: new Date(Math.max(...timestamps)).toISOString().split('T')[0],
    } : null;

    console.log('📝 [Loading State] Updating with', sampleTexts.length, 'sample texts');

    setLoadingState(prev => ({
      ...prev,
      stage: LOADING_STAGES.THREADS,
      sampleTexts,
      totalThreads: yearThreads.length,
      dateRange,
    }));

    loadingStateUpdatedRef.current = true;
  }, [isMyPage, status, lifetimeThreadsList, year]);

  // === 내 페이지 모드: 2단계 - 계산 (Firestore에 없을 때만) ===
  useEffect(() => {
    if (!isMyPage) return;
    if (!userId) return;
    if (!firestoreChecked) return; // 🚨 Firestore 확인 완료 대기
    if (status === 'ready') return; // 🚨 이미 데이터 있음 (Firestore에서 로드됨)
    if (computeStartedRef.current) return;
    if (!isLifetimeReady) return;
    if (!isFriendsReady) {
      if (lifetimeThreadsList.length > 0) {
        loadFriendsInteractionData(dispatch, lifetimeThreadsList, { year });
      }
      return;
    }

    computeStartedRef.current = true;
    console.log('📊 [Step 2] Computing year-end data...');

    const compute = async () => {
      try {
        // 스레드 분석 단계 - sampleTexts, totalThreads, dateRange 업데이트
        const yearThreads = lifetimeThreadsList.filter(t => {
          if (!t.timestamp) return false;
          return new Date(t.timestamp).getFullYear() === year;
        });
        const sampleTexts = yearThreads.slice(0, 12).map(t => t.text || '').filter(Boolean);
        const timestamps = yearThreads.map(t => new Date(t.timestamp)).filter(d => !isNaN(d));
        const dateRange = timestamps.length > 0 ? {
          start: new Date(Math.min(...timestamps)).toISOString().split('T')[0],
          end: new Date(Math.max(...timestamps)).toISOString().split('T')[0],
        } : null;

        setLoadingState(prev => ({
          ...prev,
          stage: LOADING_STAGES.THREADS,
          sampleTexts,
          totalThreads: yearThreads.length,
          dateRange,
        }));

        const summary = computeYearEndSummary(lifetimeThreadsList, year);

        if (summary.stats.totalThreads === 0) {
          setStatus('empty');
          return;
        }

        // 친구 분석 단계 - friendsAnalyzing 업데이트
        const ownerInfo = { userId: profile?.id, username: profile?.username };
        const friendsOfTheYear = computeFriendsOfTheYear(repliesByUser, ownerInfo);

        setLoadingState(prev => ({
          ...prev,
          stage: LOADING_STAGES.FRIENDS,
          friendsAnalyzing: friendsOfTheYear.slice(0, 10).map(f => ({ username: f.username })),
        }));

        // 아바타 로딩 단계
        setLoadingState(prev => ({ ...prev, stage: LOADING_STAGES.FRIENDS_AVATAR }));
        const friendsWithAvatars = await enrichFriendsWithProfilePictures(friendsOfTheYear);

        const finalData = {
          ...summary,
          friendsOfTheYear: friendsWithAvatars,
          isPublished: true,
          publishedAt: new Date().toISOString(),
        };

        const newId = await createOrUpdateYearEnd(userId, year, finalData, profile, false);

        if (newId) {
          setYearEndData(finalData);
          setYearEndId(newId);
          setIsPublished(true);
          setStatus('ready');
          console.log('✅ [Step 2] Saved:', newId);
        } else {
          setStatus('error');
        }
      } catch (error) {
        console.error('❌ Compute error:', error);
        setStatus('error');
      }
    };

    compute();
  }, [isMyPage, userId, year, firestoreChecked, status, isLifetimeReady, isFriendsReady, lifetimeThreadsList, repliesByUser, profile, dispatch]);

  // === 리셋 ===
  useEffect(() => {
    computeStartedRef.current = false;
    loadingStateUpdatedRef.current = false;
    setFirestoreChecked(false);
    setYearEndData(null);
    setYearEndId(sharedId || null);
    setStatus('loading');
    setIsPublished(false);
    setLoadingState({
      stage: LOADING_STAGES.THREADS,
      sampleTexts: [],
      totalThreads: 0,
      dateRange: null,
      friendsAnalyzing: [],
    });
  }, [year, sharedId]);

  const activitySummary = useMemo(() => {
    if (!lifetimeThreadsList || lifetimeThreadsList.length === 0) return null;
    return computeActivitySummary(lifetimeThreadsList, year);
  }, [lifetimeThreadsList, year]);

  return {
    yearEndData,
    yearEndId,
    status,
    isPublished,
    isMyPage,
    isFriendsLoading: !isFriendsReady && firestoreChecked && status !== 'ready',
    loadingState,
    activitySummary,
  };
};

export default useYearEndData;
